import { memo, useCallback, useEffect, useRef } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { useStore } from "effector-react";
import { FormInput } from "@chhjit/react-components";

import { accountApi, $accountBasic } from "../../../../entities/account/store";
import { useFeatureFlagsWithAttributes } from "../../../../entities/locations";
import { getPostalInfo } from "../../../api";

const US_POSTAL_RE = /^\d{5}$/;
const CA_POSTAL_RE = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;

const isValidFormat = (value: string) => {
  const v = value.trim();
  return US_POSTAL_RE.test(v) || CA_POSTAL_RE.test(v);
};

type PostalInputBlockProps = {
  control: any;
  label?: string;
  required?: boolean;
};

/**
 * Standalone postal code input block.
 *
 * Accepts US (12345) and Canadian (A1B 2C3) postal formats.
 * On valid input, calls getPostalInfo and writes the full postal + city/state/country
 * to account.billingAddress. Registers a hidden "servicePostal" field so the step's
 * Yup schema can gate the Next button until the postal is confirmed by the API.
 *
 * Uses two form fields:
 *   _postalDisplay — visible FormInput the user types into (not validated)
 *   servicePostal  — truthy when API confirms; empty when unconfirmed (required by Yup)
 */
export const PostalInputBlock = memo(
  ({ control, label, required }: PostalInputBlockProps) => {
    const accountBasic = useStore($accountBasic);
    const { toggles } = useFeatureFlagsWithAttributes();
    // Transport switch only (docs/migrations/getpostalinfo/07-frontend-migration.md):
    // on → hunkware-api, off → legacy php-api. Same wire shape either way.
    const useNode = Boolean(
      toggles.HunkWare_Geolocation_Address_Services_Node_Migration,
    );
    const { setError, clearErrors } = useFormContext();
    const isInitialMount = useRef(true);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const existingPostal = accountBasic.billingAddress?.postal ?? "";

    // Pre-register _postalDisplay with the existing postal so the field initialises
    // with the correct value before FormInput's internal useController runs.
    useController({
      control,
      name: "_postalDisplay",
      defaultValue: existingPostal,
    });

    // Gate field: truthy when API confirms the postal, "" when unconfirmed.
    const { field: confirmField } = useController({
      control,
      name: "servicePostal",
      defaultValue: existingPostal,
    });

    // Watch the visible field so we can debounce the API call on change.
    const displayValue: string =
      useWatch({ control, name: "_postalDisplay" }) ?? "";

    const performLookup = useCallback(
      async (postal: string) => {
        try {
          const response = await getPostalInfo({ postal }, { useNode });
          const info = response.data.postal?.[0];
          if (!info) {
            setError("_postalDisplay", {
              type: "manual",
              message: "Postal code not found. Please check and try again.",
            });
            confirmField.onChange("");
            return;
          }
          // Write the full typed postal (not the API id) so Canadian codes like T4X 1A1
          // are stored in full rather than truncated.
          accountApi.set({
            billingAddress: {
              address: accountBasic.billingAddress?.address ?? "",
              address2: accountBasic.billingAddress?.address2 ?? "",
              city: info.city,
              state: info.state,
              postal,
              country: info.country,
            },
          });
          clearErrors("_postalDisplay");
          confirmField.onChange(postal);
        } catch {
          setError("_postalDisplay", {
            type: "manual",
            message: "Unable to validate postal code. Please try again.",
          });
          confirmField.onChange("");
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [confirmField, accountBasic.billingAddress, useNode],
    );

    useEffect(() => {
      // Skip the initial render so an already-confirmed postal doesn't re-trigger the lookup.
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }
      confirmField.onChange("");
      clearErrors("_postalDisplay");
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (isValidFormat(displayValue)) {
        debounceRef.current = setTimeout(
          () => performLookup(displayValue.trim()),
          500,
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayValue]);

    useEffect(() => {
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }, []);

    return (
      <FormInput
        id="ob-form-servicePostal"
        control={control}
        name="_postalDisplay"
        label={label || "Postal / Zip Code"}
        placeholder="e.g. 12345 or A1B 2C3"
        required={required}
      />
    );
  },
);
